import { SentryTraced } from "@sentry/nestjs"
import { jwtDecode } from "jwt-decode"
import * as fs from "node:fs"
import * as os from "node:os"
import path from "node:path"
import type _keytar from "keytar"
import { TargetEnv } from "../types/environment.js"
import { EnvTokens, EnvTokensSchema, Tokens } from "../types/login.js"
import { NotAuthorizedError } from "../types/not-authorized.error.js"
import { AuthService } from "./auth.service.js"

let _keytarCache: typeof _keytar | undefined

async function importKeytar() {
  if (_keytarCache) return _keytarCache
  _keytarCache = (await import("keytar")).default
  return _keytarCache
}

export interface IVault {
  deletePassword: (serviceName: string, account: string) => Promise<void>
  getPassword: (serviceName: string, account: string) => Promise<null | string>
  setPassword: (serviceName: string, account: string, password: string) => Promise<void>
}

export class KeytarVault implements IVault {
  async deletePassword(serviceName: string, account: string): Promise<void> {
    const kt = await importKeytar()
    await kt.deletePassword(serviceName, account)
  }

  async getPassword(serviceName: string, account: string): Promise<null | string> {
    const kt = await importKeytar()
    return kt.getPassword(serviceName, account)
  }

  async setPassword(serviceName: string, account: string, password: string): Promise<void> {
    const kt = await importKeytar()
    await kt.setPassword(serviceName, account, password)
  }
}

export class VaultService {
  private readonly serviceName = "Supernova"

  constructor(
    private readonly authService: AuthService = new AuthService(),
    private readonly vault: IVault = new KeytarVault(),
  ) {}

  @SentryTraced()
  public async deleteTokensFromVault(env: TargetEnv) {
    try {
      await this.vault.deletePassword(this.serviceName, `${env}_access_token`)
      await this.vault.deletePassword(this.serviceName, `${env}_refresh_token`)
    } catch {
      // Keychain is unavailable, tokens can only live in the file cleared below
    }

    this.storeTokensInFile(env, null)
  }

  @SentryTraced()
  public async getLoggedInUserSub(env: TargetEnv): Promise<string | undefined> {
    const tokens = await this.tryGetTokensFromVault(env)
    if (!tokens) return undefined

    const decoded = jwtDecode<{ sub?: string }>(tokens.accessToken)
    return decoded.sub
  }

  @SentryTraced()
  public getTokensFromFile(env: TargetEnv): Tokens | undefined {
    const filePath = this.tokensFilePath()

    if (!fs.existsSync(filePath)) {
      return undefined
    }

    try {
      const fileContent = fs.readFileSync(filePath, "utf8")
      const data = JSON.parse(fileContent)
      return data[env] ?? undefined
    } catch {
      return undefined
    }
  }

  @SentryTraced()
  public async tryGetTokensFromVault(env: TargetEnv): Promise<Tokens | null> {
    if (process.env.SUPERNOVA_TOKEN) {
      return { accessToken: process.env.SUPERNOVA_TOKEN }
    }

    let accessToken: string | undefined
    let refreshToken: string | undefined
    try {
      accessToken = (await this.vault.getPassword(this.serviceName, `${env}_access_token`)) ?? undefined
      refreshToken = (await this.vault.getPassword(this.serviceName, `${env}_refresh_token`)) ?? undefined
    } catch {
      // Keychain is unavailable, the file fallback below covers it
    }

    if (!accessToken) {
      const tokens = this.getTokensFromFile(env)
      accessToken = tokens?.accessToken
      refreshToken = tokens?.refreshToken
    } else if (!refreshToken) {
      // Keychain write can partially fail, leaving the refresh token only in the file
      const tokens = this.getTokensFromFile(env)
      if (tokens?.accessToken === accessToken) {
        refreshToken = tokens.refreshToken
      }
    }

    if (!accessToken) {
      return null
    }

    const freshTokens = refreshToken ? await this.refreshTokens(env, accessToken, refreshToken) : undefined
    if (freshTokens) {
      return freshTokens
    }

    return { accessToken, refreshToken }
  }

  @SentryTraced()
  public async getTokensFromVault(env: TargetEnv): Promise<Tokens> {
    const tokens = await this.tryGetTokensFromVault(env)
    if (!tokens) throw new NotAuthorizedError()
    return tokens
  }

  @SentryTraced()
  public async refreshTokens(env: TargetEnv, accessToken: string, refreshToken: string): Promise<Tokens | undefined> {
    const decoded = jwtDecode<{ exp?: number }>(accessToken)
    const currentTime = Math.floor(Date.now() / 1000)
    if (decoded.exp && decoded.exp < currentTime + 300) {
      const freshTokens = await this.authService.refreshToken(env, refreshToken)
      if (!freshTokens) {
        await this.deleteTokensFromVault(env)
        throw new NotAuthorizedError()
      }

      await this.storeTokensToVault(freshTokens, env)
      return freshTokens
    }

    return undefined
  }

  @SentryTraced()
  public storeTokensInFile(env: TargetEnv, tokens: null | Tokens) {
    const supernovaDir = this.supernovaDir()
    const filePath = path.join(supernovaDir, "tokens.json")

    if (!fs.existsSync(supernovaDir)) {
      fs.mkdirSync(supernovaDir, { recursive: true })
    }

    let data: EnvTokens = {}
    if (fs.existsSync(filePath)) {
      try {
        const fileContent = fs.readFileSync(filePath, "utf8")
        data = EnvTokensSchema.parse(JSON.parse(fileContent))
      } catch {
        // Corrupt file is discarded and rewritten from scratch
      }
    }

    if (tokens) data[env] = tokens
    else delete data[env]

    fs.writeFileSync(filePath, JSON.stringify(data, null, 2), "utf8")
  }

  @SentryTraced()
  public async storeTokensToVault({ accessToken, refreshToken }: Tokens, env: TargetEnv) {
    try {
      await this.vault.setPassword(this.serviceName, `${env}_access_token`, accessToken)
      if (refreshToken) {
        await this.vault.setPassword(this.serviceName, `${env}_refresh_token`, refreshToken)
      }
    } catch {
      this.storeTokensInFile(env, { accessToken, refreshToken })
    }
  }

  public supernovaDir(): string {
    const homeDir = os.homedir()
    return path.join(homeDir, ".supernova")
  }

  public tokensFilePath(): string {
    const homeDir = this.supernovaDir()
    return path.join(homeDir, "tokens.json")
  }
}
